import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyStore, FamilyMember } from '../../store/familyStore';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import ImageCropper from '../../components/ImageCropper';
import BottomSheet from '../../components/mobile/BottomSheet';
import ColorPicker from '../../components/ColorPicker';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Icon } from '../../utils/icons';
import { getApiUrl, getApiKey } from '../../utils/apiConfig';
import { useToastStore } from '../../store/toastStore';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function MobileFamily() {
  const navigate = useNavigate();
  const { 
    family, 
    invitations,
    isLoading, 
    error,
    fetchFamily, 
    createFamily,
    updateFamily,
    addMember, 
    updateMember, 
    removeMember,
    createInvitation,
    listInvitations,
    resendInvitation,
    cancelInvitation,
    clearError
  } = useFamilyStore();
  const { isAuthenticated, signOut, signOutFromAllDevices } = useAuthStore();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const { showToast } = useToastStore();
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showSignOutMenu, setShowSignOutMenu] = useState(false);
  
  // Photo upload state
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [croppingForFamily, setCroppingForFamily] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isSavingFamily, setIsSavingFamily] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [isInvitingMember, setIsInvitingMember] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editMemberFileInputRef = useRef<HTMLInputElement>(null);
  const familyPhotoInputRef = useRef<HTMLInputElement>(null);
  
  // Form states
  const [showCreateFamilySheet, setShowCreateFamilySheet] = useState(false);
  const [showAddMemberSheet, setShowAddMemberSheet] = useState(false);
  const [showInviteMemberSheet, setShowInviteMemberSheet] = useState(false);
  const [showEditFamilySheet, setShowEditFamilySheet] = useState(false);
  const [showRemoveMemberConfirm, setShowRemoveMemberConfirm] = useState(false);
  
  // Create Family form
  const [familyName, setFamilyName] = useState('');
  
  // Add/Invite Member form
  const [memberEmail, setMemberEmail] = useState('');
  const [memberName, setMemberName] = useState('');
  const [memberRole, setMemberRole] = useState<'parent' | 'child' | 'adult'>('parent');
  const [memberColor, setMemberColor] = useState('#3b82f6');
  const [memberAvatar, setMemberAvatar] = useState('');
  
  // Edit Family form
  const [editFamilyName, setEditFamilyName] = useState('');
  const [familyLocationCity, setFamilyLocationCity] = useState('');
  const [familyLocationLat, setFamilyLocationLat] = useState<string>('');
  const [familyLocationLng, setFamilyLocationLng] = useState<string>('');
  
  // Remove member
  const [pendingRemoveMemberId, setPendingRemoveMemberId] = useState<string | null>(null);
  
  // Resend invitation states
  const [resendingInvitations, setResendingInvitations] = useState<Set<string>>(new Set());
  const [sentInvitations, setSentInvitations] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isAuthenticated) {
      fetchFamily().catch((error) => {
        console.error('Error fetching family:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load family', 'error');
      });
    }
  }, [isAuthenticated, fetchFamily, showToast]);

  // Load invitations when family is available
  useEffect(() => {
    if (family) {
      const currentUserId = getCurrentUserId();
      const currentUserMember = family.members.find(m => m.userId === currentUserId);
      const canManageFamily = currentUserMember && (currentUserMember.role === 'owner' || currentUserMember.role === 'parent');
      if (canManageFamily) {
        listInvitations('pending').catch((error) => {
          console.error('Error loading invitations:', error);
          showToast(error instanceof Error ? error.message : 'Failed to load invitations', 'error');
        });
      }
    }
  }, [family, listInvitations, showToast]);

  // Initialize edit form when family loads
  useEffect(() => {
    if (family) {
      setEditFamilyName(family.name);
      setFamilyLocationCity(family.location?.city || '');
      setFamilyLocationLat(family.location?.latitude?.toString() || '');
      setFamilyLocationLng(family.location?.longitude?.toString() || '');
    }
  }, [family]);

  // Get current user ID from token
  const getCurrentUserId = (): string | null => {
    try {
      const { idToken } = useAuthStore.getState();
      if (!idToken) return null;
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return payload.sub;
    } catch {
      return null;
    }
  };

  const currentUserId = getCurrentUserId();
  const currentUserMember = family?.members.find(m => m.userId === currentUserId);
  const canManageFamily = currentUserMember && (currentUserMember.role === 'owner' || currentUserMember.role === 'parent');
  
  // Check if current user can edit this member's photo
  const canEditMemberPhoto = (member: FamilyMember) => {
    if (!currentUserId || !family) return false;
    
    // Can edit own photo
    if (member.userId === currentUserId) return true;
    
    // Parents/owners can edit children's photos
    const currentUserMember = family.members.find(m => m.userId === currentUserId);
    if (!currentUserMember) return false;
    
    const isParentOrOwner = currentUserMember.role === 'owner' || currentUserMember.role === 'parent';
    const isChild = member.role === 'child';
    
    return isParentOrOwner && isChild;
  };

  // Handle Create Family
  const handleCreateFamily = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSavingFamily) return;
    setIsSavingFamily(true);
    try {
      await createFamily(familyName);
      setShowCreateFamilySheet(false);
      setFamilyName('');
      // Refresh family data
      await fetchFamily();
      showToast('Family created', 'success');
    } catch (err) {
      console.error('Error creating family:', err);
      showToast(err instanceof Error ? err.message : 'Failed to create family', 'error');
    } finally {
      setIsSavingFamily(false);
    }
  };

  // Handle Add Member
  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (memberRole !== 'child' && !memberEmail.trim()) {
      showToast('Email is required for parents and adults', 'error');
      return;
    }
    if (isAddingMember) return;
    setIsAddingMember(true);
    try {
      if (memberRole !== 'child' && memberEmail.trim()) {
        try {
          const result = await createInvitation(memberEmail.trim(), memberName, memberRole, memberColor);
          if (result.action === 'invited') {
            setShowAddMemberSheet(false);
            resetMemberForm();
            showToast('Invitation sent', 'success');
            return;
          }
        } catch (inviteErr: any) {
          if (!inviteErr.message?.includes('already exists') && !inviteErr.message?.includes('already a member')) {
            throw inviteErr;
          }
        }
      }
      
      await addMember(memberEmail || undefined, memberName, memberRole, memberColor, memberAvatar || undefined);
      setShowAddMemberSheet(false);
      resetMemberForm();
      showToast('Member added', 'success');
    } catch (err) {
      console.error('Error adding member:', err);
      showToast(err instanceof Error ? err.message : 'Failed to add member', 'error');
    } finally {
      setIsAddingMember(false);
    }
  };

  // Handle Invite Member
  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberEmail.trim()) {
      showToast('Email is required', 'error');
      return;
    }
    if (memberRole === 'child') {
      showToast('Children cannot be invited. Add them directly.', 'error');
      return;
    }
    if (isInvitingMember) return;
    setIsInvitingMember(true);
    try {
      await createInvitation(memberEmail.trim(), memberName, memberRole, memberColor);
      setShowInviteMemberSheet(false);
      resetMemberForm();
      showToast('Invitation sent', 'success');
    } catch (err) {
      console.error('Error sending invitation:', err);
      showToast(err instanceof Error ? err.message : 'Failed to send invitation', 'error');
    } finally {
      setIsInvitingMember(false);
    }
  };

  // Handle Update Family
  const handleUpdateFamily = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSavingFamily) return;
    setIsSavingFamily(true);
    try {
      const updates: { name?: string; location?: { city?: string; latitude?: number; longitude?: number } } = {};
      
      if (editFamilyName !== family?.name) {
        updates.name = editFamilyName;
      }

      const location: { city?: string; latitude?: number; longitude?: number } = {};
      if (familyLocationCity !== (family?.location?.city || '')) {
        location.city = familyLocationCity.trim() || undefined;
      }
      if (familyLocationLat !== (family?.location?.latitude?.toString() || '')) {
        location.latitude = familyLocationLat.trim() ? parseFloat(familyLocationLat) : undefined;
      }
      if (familyLocationLng !== (family?.location?.longitude?.toString() || '')) {
        location.longitude = familyLocationLng.trim() ? parseFloat(familyLocationLng) : undefined;
      }

      if (location.city !== undefined || location.latitude !== undefined || location.longitude !== undefined) {
        updates.location = location;
      }

      if (Object.keys(updates).length > 0) {
        await updateFamily(updates);
      }
      setShowEditFamilySheet(false);
      if (Object.keys(updates).length > 0) {
        showToast('Family updated', 'success');
      }
    } catch (err) {
      console.error('Error updating family:', err);
      showToast(err instanceof Error ? err.message : 'Failed to update family', 'error');
    } finally {
      setIsSavingFamily(false);
    }
  };

  // Handle Remove Member
  const handleRemoveMember = (memberId: string) => {
    setPendingRemoveMemberId(memberId);
    setShowRemoveMemberConfirm(true);
  };

  const handleConfirmRemoveMember = async () => {
    if (!pendingRemoveMemberId) return;
    if (isRemovingMember) return;
    setIsRemovingMember(true);
    try {
      await removeMember(pendingRemoveMemberId);
      setShowRemoveMemberConfirm(false);
      setPendingRemoveMemberId(null);
      showToast('Member removed', 'success');
    } catch (err) {
      setShowRemoveMemberConfirm(false);
      setPendingRemoveMemberId(null);
      console.error('Error removing member:', err);
      showToast(err instanceof Error ? err.message : 'Failed to remove member', 'error');
    } finally {
      setIsRemovingMember(false);
    }
  };

  // Handle Resend Invitation
  const handleResendInvitation = async (invitationId: string) => {
    // Optimistic update: show "Sending" state immediately
    setResendingInvitations(prev => new Set(prev).add(invitationId));
    
    try {
      await resendInvitation(invitationId);
      // Show "Sent" state
      setResendingInvitations(prev => {
        const next = new Set(prev);
        next.delete(invitationId);
        return next;
      });
      setSentInvitations(prev => new Set(prev).add(invitationId));
      
      // After 2 seconds, revert to "Resend"
      setTimeout(() => {
        setSentInvitations(prev => {
          const next = new Set(prev);
          next.delete(invitationId);
          return next;
        });
      }, 2000);
      showToast('Invitation resent', 'success');
    } catch (err) {
      // On error, revert to "Resend" state
      setResendingInvitations(prev => {
        const next = new Set(prev);
        next.delete(invitationId);
        return next;
      });
      console.error('Error resending invitation:', err);
      showToast(err instanceof Error ? err.message : 'Failed to resend invitation', 'error');
    }
  };

  // Handle Cancel Invitation
  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId);
      showToast('Invitation canceled', 'success');
    } catch (err) {
      console.error('Error canceling invitation:', err);
      showToast(err instanceof Error ? err.message : 'Failed to cancel invitation', 'error');
    }
  };

  // Reset member form
  const resetMemberForm = () => {
    setMemberEmail('');
    setMemberName('');
    setMemberRole('parent');
    setMemberColor('#3b82f6');
    setMemberAvatar('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle file select for member photo or family photo
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      showToast('File size exceeds 5MB. Please choose a smaller image.', 'error');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Invalid file type. Please choose a JPEG, PNG, GIF, or WebP image.', 'error');
      return;
    }

    const isFamilyPhoto = e.target === familyPhotoInputRef.current;
    if (isFamilyPhoto) {
      setCroppingForFamily(true);
      setSelectedMemberId(null);
    } else {
      setCroppingForFamily(false);
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const imageUrl = event.target?.result as string;
      setSelectedImage(imageUrl);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleChangePhotoClick = (memberId: string) => {
    setSelectedMemberId(memberId);
    fileInputRef.current?.click();
  };

  const handleCropComplete = async (croppedImageDataUrl: string) => {
    setShowCropper(false);
    setUploading(true);

    try {
      const response = await fetch(croppedImageDataUrl);
      const blob = await response.blob();

      const MAX_FILE_SIZE = 500 * 1024;
      let finalBlob = blob;
      
      if (blob.size > MAX_FILE_SIZE) {
        const img = new Image();
        img.src = croppedImageDataUrl;
        await new Promise((resolve) => {
          img.onload = resolve;
        });

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not create canvas context');

        let targetSize = 300;
        let quality = 0.75;
        
        canvas.width = targetSize;
        canvas.height = targetSize;
        ctx.drawImage(img, 0, 0, targetSize, targetSize);
        
        let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        let compressedBlob = await (await fetch(compressedDataUrl)).blob();
        
        while (compressedBlob.size > MAX_FILE_SIZE && quality > 0.5) {
          quality -= 0.1;
          compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          compressedBlob = await (await fetch(compressedDataUrl)).blob();
        }
        
        finalBlob = compressedBlob;
      }

      const API_URL = getApiUrl();
      const API_KEY = getApiKey();
      const { idToken } = useAuthStore.getState();

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (API_KEY) {
        headers['X-Api-Key'] = API_KEY;
      }
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      const uploadUrlResponse = await fetch(`${API_URL}/upload/url`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          fileName: croppingForFamily ? 'family-photo.jpg' : 'member-photo.jpg',
          fileType: 'image/jpeg',
          fileSize: finalBlob.size,
          ...(croppingForFamily && { purpose: 'familyAvatar' }),
        }),
      });

      if (!uploadUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, fileUrl } = await uploadUrlResponse.json();

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: finalBlob,
        headers: {
          'Content-Type': 'image/jpeg',
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image');
      }

      if (croppingForFamily) {
        await updateFamily({ avatar: fileUrl });
        setCroppingForFamily(false);
        showToast('Family photo updated', 'success');
        return;
      }
      if (selectedMemberId) {
        await updateMember(selectedMemberId, { avatar: fileUrl });
        showToast('Photo updated', 'success');
        setSelectedMemberId(null);
      } else if (memberAvatar === '') {
        // For new member
        setMemberAvatar(fileUrl);
        showToast('Photo ready to save', 'success');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      showToast(error?.message || 'Failed to upload image', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Early returns
  if (!isAuthenticated) {
    navigate('/login', { replace: true });
    return null;
  }

  if (isLoading && !family) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${tokens.border.accent}`}></div>
      </div>
    );
  }

  // No family - show create family option
  if (!family && !showCreateFamilySheet) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <div className="text-center w-full max-w-md">
          <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-4 font-serif`}>Create Your Family</h1>
          <p className={`text-sm ${tokens.text.muted} mb-6`}>
            Get started by creating a family group to share calendars and manage activities together.
          </p>
          <button
            onClick={() => setShowCreateFamilySheet(true)}
            className={`w-full ${tokens.button.primary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px] mb-3`}
          >
            Create Family
          </button>
          <button
            onClick={() => {
              signOut();
              navigate('/login');
            }}
            className={`w-full ${tokens.button.secondary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px] flex items-center justify-center gap-2`}
          >
            <Icon name="sign-out" size="sm" className={tokens.text.primary} />
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto scrollable">
      {/* Header */}
      <div className={`sticky top-0 ${tokens.card.bg} ${tokens.border.light} border-b z-10 px-4 py-2 safe-area-inset-top`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {family && canManageFamily && (
              <>
                <input
                  ref={familyPhotoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <button
                  type="button"
                  onClick={() => familyPhotoInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-shrink-0 rounded-full overflow-hidden w-12 h-12 flex items-center justify-center bg-gray-300 min-h-[48px] min-w-[48px]"
                  style={family.avatar ? undefined : { backgroundColor: '#3b82f6' }}
                  aria-label="Family photo"
                >
                  {!family.avatar && (
                    <span className="text-white text-lg font-bold">
                      {family.name?.charAt(0).toUpperCase() || 'F'}
                    </span>
                  )}
                  {family.avatar && (
                    <img
                      src={family.avatar}
                      alt="Family"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                </button>
              </>
            )}
            <div className="min-w-0">
              <h1 className={`text-2xl font-bold ${tokens.text.primary} font-serif truncate`}>{family?.name || 'Family'}</h1>
              {family && (
                <p className={`text-sm ${tokens.text.muted} mt-1`}>
                  {family.members.length} member{family.members.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 items-center flex-shrink-0">
            {family && canManageFamily && (
              <>
                <button
                  onClick={() => setShowEditFamilySheet(true)}
                  className={`px-3 py-2 ${tokens.button.secondary} ${tokens.text.primary} font-semibold rounded-landing transition-colors touch-target min-h-[44px] min-w-[44px] flex items-center justify-center`}
                  aria-label="Edit family"
                >
                  <Icon name="edit" size="sm" />
                </button>
                <button
                  onClick={() => setShowAddMemberSheet(true)}
                  className={`px-3 py-2 ${tokens.button.primary} font-semibold rounded-landing transition-colors touch-target min-h-[44px] min-w-[44px] flex items-center justify-center`}
                  aria-label="Add member"
                >
                  <Icon name="plus" size="sm" />
                </button>
              </>
            )}
            <div className="relative">
              <button
                onClick={() => setShowSignOutMenu(!showSignOutMenu)}
                className={`px-3 py-2 ${tokens.button.secondary} ${tokens.text.primary} font-semibold rounded-landing transition-colors touch-target min-h-[44px] min-w-[44px] flex items-center justify-center`}
                aria-label="Sign out"
              >
                <Icon name="sign-out" size="sm" />
              </button>
              {showSignOutMenu && (
                <div className={`absolute right-0 top-full mt-2 ${tokens.card.bg} ${tokens.border.light} border rounded-landing ${tokens.card.shadow} min-w-[200px] z-50`}>
                  <button
                    onClick={() => {
                      signOut();
                      navigate('/login');
                      setShowSignOutMenu(false);
                    }}
                    className={`w-full ${tokens.text.primary} ${tokens.card.bg} hover:bg-opacity-80 px-4 py-3 text-left transition-colors min-h-[48px] flex items-center`}
                  >
                    Sign Out (This Device)
                  </button>
                  <button
                    onClick={() => {
                      setShowSignOutMenu(false);
                      setShowSignOutConfirm(true);
                    }}
                    className={`w-full ${tokens.text.primary} ${tokens.card.bg} hover:bg-opacity-80 px-4 py-3 text-left transition-colors min-h-[48px] flex items-center border-t ${tokens.border.light}`}
                  >
                    Sign Out (All Devices)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`mx-4 mt-2 p-3 ${isDarkMode ? 'bg-red-900/30 border-red-700 text-red-300' : 'bg-red-100 border-red-400 text-red-800'} rounded-landing text-sm border flex items-center justify-between`}>
          <span>{error}</span>
          <button
            onClick={clearError}
            className="text-red-700 hover:text-red-900 font-bold ml-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            ×
          </button>
        </div>
      )}

      {/* Pending Invitations */}
      {family && canManageFamily && invitations.length > 0 && (
        <div className="px-3 py-2">
          <h2 className={`text-lg font-semibold ${tokens.text.primary} mb-2 font-serif`}>Pending Invitations</h2>
          <div className="space-y-2">
            {invitations.map((invitation) => {
              const expiresAt = new Date(invitation.expiresAt * 1000);
              const daysUntilExpiry = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
              
              return (
                <div
                  key={invitation.invitationId}
                  className={`${tokens.card.bg} rounded-landing p-3 ${tokens.border.light} border ${tokens.card.shadow}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                        style={{ backgroundColor: invitation.color }}
                      >
                        {invitation.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold ${tokens.text.primary} truncate`}>{invitation.name}</div>
                        <div className={`text-sm ${tokens.text.muted} truncate`}>{invitation.email}</div>
                        <div className={`text-xs ${tokens.text.muted} mt-1`}>
                          Expires in {daysUntilExpiry} {daysUntilExpiry === 1 ? 'day' : 'days'}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleResendInvitation(invitation.invitationId)}
                        disabled={resendingInvitations.has(invitation.invitationId) || sentInvitations.has(invitation.invitationId)}
                        className={`px-3 py-2 font-semibold rounded-landing transition-colors touch-target min-h-[44px] ${
                          sentInvitations.has(invitation.invitationId)
                            ? isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'
                            : resendingInvitations.has(invitation.invitationId)
                            ? isDarkMode ? 'bg-gray-900/30 text-gray-400' : 'bg-gray-100 text-gray-500'
                            : isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {sentInvitations.has(invitation.invitationId)
                          ? 'Sent'
                          : resendingInvitations.has(invitation.invitationId)
                          ? 'Sending...'
                          : 'Resend'}
                      </button>
                      <button
                        onClick={() => handleCancelInvitation(invitation.invitationId)}
                        className={`px-3 py-2 ${isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'} font-semibold rounded-landing transition-colors touch-target min-h-[44px]`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Family Members */}
      {family && (
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <h2 className={`text-lg font-semibold ${tokens.text.primary} font-serif`}>Family Members</h2>
            {canManageFamily && (
              <button
                onClick={() => setShowInviteMemberSheet(true)}
                className={`px-3 py-1.5 text-sm ${tokens.button.primary} font-semibold rounded-landing transition-colors touch-target min-h-[36px]`}
              >
                Invite
              </button>
            )}
          </div>
          
          <div className="space-y-2">
            {family.members.map((member) => {
              const isOwnCard = member.userId === currentUserId;
              return (
                <div
                  key={member.userId}
                  className={`${tokens.card.bg} rounded-landing p-3 ${tokens.border.light} border ${tokens.card.shadow} min-h-[80px]`}
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div className="relative">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold border-2 relative z-0"
                        style={{ 
                          backgroundColor: member.color,
                          borderColor: member.color,
                        }}
                      >
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      {member.avatar && (
                        <img
                          src={member.avatar}
                          alt={member.name}
                          className="absolute inset-0 w-14 h-14 rounded-full object-cover border-2 z-10"
                          style={{ 
                            borderColor: member.color,
                            backgroundColor: member.color
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                    </div>

                    {/* Member Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className={`text-base font-semibold ${tokens.text.primary}`}>
                          {member.name}
                          {isOwnCard && <span className={`text-xs ${tokens.text.muted} ml-2`}>(You)</span>}
                        </h3>
                        <div className="flex gap-2">
                          {canEditMemberPhoto(member) && (
                            <button
                              onClick={() => handleChangePhotoClick(member.userId || '')}
                              disabled={uploading}
                              className={`px-3 py-1.5 text-xs ${tokens.button.primary} font-semibold rounded-landing transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-target min-h-[36px]`}
                              aria-label="Change photo"
                            >
                              {uploading ? '...' : 'Photo'}
                            </button>
                          )}
                          {canManageFamily && member.role !== 'owner' && (
                            <button
                              onClick={() => handleRemoveMember(member.userId || '')}
                              className={`px-3 py-1.5 text-xs ${isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'} font-semibold rounded-landing transition-colors touch-target min-h-[36px]`}
                              aria-label="Remove member"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      {member.email && (
                        <p className={`text-sm ${tokens.text.muted} truncate`}>{member.email}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-1 rounded-landing text-xs font-medium ${
                          member.role === 'owner' ? 'bg-purple-100 text-purple-800' :
                          member.role === 'parent' ? 'bg-blue-100 text-blue-800' :
                          member.role === 'child' ? 'bg-green-100 text-green-800' :
                          `${tokens.button.secondary} ${tokens.text.primary}`
                        }`}>
                          {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

        {/* Image Cropper */}
        {showCropper && selectedImage && (
          <ImageCropper
            image={selectedImage}
            onCrop={handleCropComplete}
            onCancel={() => {
              setShowCropper(false);
              setSelectedImage('');
              setSelectedMemberId(null);
              setCroppingForFamily(false);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
              if (familyPhotoInputRef.current) {
                familyPhotoInputRef.current.value = '';
              }
            }}
            size={200}
          />
        )}
      </div>

      {/* Create Family Bottom Sheet */}
      <BottomSheet
        isOpen={showCreateFamilySheet}
        onClose={() => {
          setShowCreateFamilySheet(false);
          setFamilyName('');
        }}
        title="Create Family"
      >
        <form onSubmit={handleCreateFamily} className="p-4 space-y-4">
          <div>
            <label htmlFor="familyName" className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
              Family Name *
            </label>
            <input
              type="text"
              id="familyName"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              required
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="Enter family name"
              autoFocus
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowCreateFamilySheet(false);
                setFamilyName('');
              }}
              className={`flex-1 ${tokens.button.secondary} ${tokens.text.primary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px]`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`flex-1 ${tokens.button.primary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
              disabled={isSavingFamily}
            >
              {isSavingFamily ? <LoadingSpinner size="sm" label="Creating family" /> : 'Create'}
            </button>
          </div>
        </form>
      </BottomSheet>

      {/* Add Member Bottom Sheet */}
      <BottomSheet
        isOpen={showAddMemberSheet}
        onClose={() => {
          setShowAddMemberSheet(false);
          resetMemberForm();
        }}
        title="Add Family Member"
      >
        <form onSubmit={handleAddMember} className="p-4 space-y-4">
          <div>
            <label htmlFor="addMemberRole" className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
              Relationship *
            </label>
            <select
              id="addMemberRole"
              value={memberRole}
              onChange={(e) => setMemberRole(e.target.value as 'parent' | 'child' | 'adult')}
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
            >
              <option value="parent">Parent</option>
              <option value="adult">Adult</option>
              <option value="child">Child</option>
            </select>
          </div>
          <div>
            <label htmlFor="addMemberName" className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
              Name *
            </label>
            <input
              type="text"
              id="addMemberName"
              value={memberName}
              onChange={(e) => setMemberName(e.target.value)}
              required
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="Member name"
            />
          </div>
          <div>
            <label htmlFor="addMemberEmail" className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
              Email {memberRole === 'child' && <span className="text-gray-500 font-normal">(optional)</span>}
            </label>
            <input
              type="email"
              id="addMemberEmail"
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              required={memberRole !== 'child'}
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="member@example.com"
            />
            {memberRole === 'child' && (
              <p className={`mt-1 text-xs ${tokens.text.muted}`}>Children don't need an email address in kiosk mode</p>
            )}
          </div>
          <div>
            <ColorPicker
              value={memberColor}
              onChange={setMemberColor}
              label="Color"
            />
          </div>
          <div>
            <label htmlFor="addMemberAvatar" className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
              Photo (optional)
            </label>
            <input
              ref={editMemberFileInputRef}
              type="file"
              id="addMemberAvatar"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => editMemberFileInputRef.current?.click()}
                disabled={uploading}
                className={`px-4 py-3 ${tokens.button.secondary} ${tokens.text.primary} font-semibold rounded-landing transition-colors touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]`}
              >
                {uploading ? 'Uploading...' : memberAvatar ? 'Change Photo' : 'Choose Photo'}
              </button>
              {memberAvatar && (
                <img
                  src={memberAvatar}
                  alt="Preview"
                  className="w-16 h-16 rounded-full object-cover border-2 border-gray-300"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
            </div>
            <p className={`mt-2 text-xs ${tokens.text.muted}`}>Max file size: 5MB. Supported formats: JPEG, PNG, GIF, WebP</p>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowAddMemberSheet(false);
                resetMemberForm();
              }}
              className={`flex-1 ${tokens.button.secondary} ${tokens.text.primary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px]`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`flex-1 ${tokens.button.primary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
              disabled={isAddingMember}
            >
              {isAddingMember ? <LoadingSpinner size="sm" label="Adding member" /> : 'Add Member'}
            </button>
          </div>
        </form>
      </BottomSheet>

      {/* Invite Member Bottom Sheet */}
      <BottomSheet
        isOpen={showInviteMemberSheet}
        onClose={() => {
          setShowInviteMemberSheet(false);
          resetMemberForm();
        }}
        title="Invite Family Member"
      >
        <form onSubmit={handleInviteMember} className="p-4 space-y-4">
          <div>
            <label htmlFor="inviteEmail" className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
              Email *
            </label>
            <input
              type="email"
              id="inviteEmail"
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              required
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="email@example.com"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="inviteName" className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
              Name *
            </label>
            <input
              type="text"
              id="inviteName"
              value={memberName}
              onChange={(e) => setMemberName(e.target.value)}
              required
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="Member name"
            />
          </div>
          <div>
            <label htmlFor="inviteRole" className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
              Role *
            </label>
            <select
              id="inviteRole"
              value={memberRole}
              onChange={(e) => setMemberRole(e.target.value as 'parent' | 'adult')}
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
            >
              <option value="parent">Parent</option>
              <option value="adult">Adult</option>
            </select>
          </div>
          <div>
            <ColorPicker
              value={memberColor}
              onChange={setMemberColor}
              label="Color"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowInviteMemberSheet(false);
                resetMemberForm();
              }}
              className={`flex-1 ${tokens.button.secondary} ${tokens.text.primary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px]`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`flex-1 ${tokens.button.primary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
              disabled={isInvitingMember}
            >
              {isInvitingMember ? <LoadingSpinner size="sm" label="Sending invitation" /> : 'Send Invitation'}
            </button>
          </div>
        </form>
      </BottomSheet>

      {/* Edit Family Bottom Sheet */}
      <BottomSheet
        isOpen={showEditFamilySheet}
        onClose={() => {
          setShowEditFamilySheet(false);
          if (family) {
            setEditFamilyName(family.name);
            setFamilyLocationCity(family.location?.city || '');
            setFamilyLocationLat(family.location?.latitude?.toString() || '');
            setFamilyLocationLng(family.location?.longitude?.toString() || '');
          }
        }}
        title="Edit Family"
      >
        <form onSubmit={handleUpdateFamily} className="p-4 space-y-4">
          <div>
            <label htmlFor="editFamilyName" className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
              Family Name *
            </label>
            <input
              type="text"
              id="editFamilyName"
              value={editFamilyName}
              onChange={(e) => setEditFamilyName(e.target.value)}
              required
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
            />
          </div>
          {family && (
            <div>
              <span className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>Family photo</span>
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0 overflow-hidden"
                  style={family.avatar ? undefined : { backgroundColor: '#3b82f6' }}
                >
                  {!family.avatar && (family.name?.charAt(0).toUpperCase() || 'F')}
                  {family.avatar && (
                    <img src={family.avatar} alt="Family" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => familyPhotoInputRef.current?.click()}
                    className={`text-sm ${tokens.button.primary} font-semibold py-2 px-4 rounded-landing transition-colors touch-target min-h-[44px]`}
                  >
                    {family.avatar ? 'Change' : 'Add photo'}
                  </button>
                  {family.avatar && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await updateFamily({ avatar: '' });
                          showToast('Family photo removed', 'success');
                          setShowEditFamilySheet(false);
                        } catch (err) {
                          showToast('Failed to remove photo', 'error');
                        }
                      }}
                      className={`text-sm ${isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'} font-semibold py-2 px-4 rounded-landing transition-colors touch-target min-h-[44px]`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          <div>
            <label htmlFor="editFamilyLocationCity" className={`block text-sm font-medium ${tokens.text.primary} mb-2`}>
              Location (for weather)
            </label>
            <input
              type="text"
              id="editFamilyLocationCity"
              value={familyLocationCity}
              onChange={(e) => setFamilyLocationCity(e.target.value)}
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent text-base min-h-[48px] ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="e.g., San Francisco, CA or 94103"
            />
            <p className={`mt-1 text-xs ${tokens.text.muted}`}>
              Enter city name, US zip code, or coordinates (e.g., "37.7749,-122.4194")
            </p>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowEditFamilySheet(false);
                if (family) {
                  setEditFamilyName(family.name);
                  setFamilyLocationCity(family.location?.city || '');
                  setFamilyLocationLat(family.location?.latitude?.toString() || '');
                  setFamilyLocationLng(family.location?.longitude?.toString() || '');
                }
              }}
              className={`flex-1 ${tokens.button.secondary} ${tokens.text.primary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px]`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`flex-1 ${tokens.button.primary} font-semibold py-3 px-6 rounded-landing transition-colors touch-target min-h-[48px] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
              disabled={isSavingFamily}
            >
              {isSavingFamily ? <LoadingSpinner size="sm" label="Saving family" /> : 'Save'}
            </button>
          </div>
        </form>
      </BottomSheet>

      {/* Remove Member Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRemoveMemberConfirm}
        title="Remove Member"
        message="Are you sure you want to remove this member? This action cannot be undone."
        confirmText={isRemovingMember ? 'Removing...' : 'Remove'}
        cancelText="Cancel"
        onConfirm={handleConfirmRemoveMember}
        onCancel={() => {
          setShowRemoveMemberConfirm(false);
          setPendingRemoveMemberId(null);
        }}
        isDestructive={true}
      />

      {/* Sign Out Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSignOutConfirm}
        title="Sign Out from All Devices"
        message="This will sign you out from all devices where you're currently signed in. You'll need to sign in again on all devices. Continue?"
        confirmText="Sign Out Everywhere"
        cancelText="Cancel"
        onConfirm={async () => {
          await signOutFromAllDevices();
          navigate('/login');
          setShowSignOutConfirm(false);
        }}
        onCancel={() => setShowSignOutConfirm(false)}
        isDestructive={true}
      />
    </div>
  );
}
